// src/utils/zoneUtils.js
import React from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip } from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

export const zoneColors = {
  Z1: '#A2D5AB',
  Z2: '#7FB3D5',
  Z3: '#F9E79F',
  Z4: '#F5B041',
  Z5: '#EC7063',
  Z5a: '#EC7063',
  Z5b: '#C0392B',
  Z5c: '#6C3483',
  Z6: '#884EA0',
  Z7: '#4A235A',
};

export const formatTime = (totalSeconds) => {
  const m = Math.floor(totalSeconds / 60);
  const s = Math.round(totalSeconds % 60);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

export const getChartConfig = (data, title) => ({
  labels: data.map(zone => zone.name.split(' ')[0]),
  datasets: [
    {
      label: '% de tiempo',
      data: data.map(zone => zone.percentage),
      backgroundColor: data.map(zone => zoneColors[zone.name.split(' ')[0]] || 'rgba(75, 192, 192, 0.6)'),
      borderColor: data.map(zone => zoneColors[zone.name.split(' ')[0]] || 'rgba(75, 192, 192, 1)'),
      borderWidth: 1
    }
  ],
  options: {
    indexAxis: 'y',
    responsive: true,
    plugins: {
      legend: { display: false },
      title: { display: true, text: title }
    },
    scales: {
      x: {
        min: 0,
        max: 100,
        ticks: {
          callback: (value) => `${value}%`
        }
      }
    }
  }
});

export const renderLegend = (data) => (
  <div className="zone-legend" style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '10px' }}>
    {data.map(zone => {
      const label = zone.name.split(' ')[0];
      return (
        <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{
            width: '14px',
            height: '14px',
            backgroundColor: zoneColors[label] || '#ccc',
            borderRadius: '2px'
          }}></div>
          <span>{label}</span>
        </div>
      );
    })}
  </div>
);

export const renderZoneDetails = (data) => (
  <div style={{ marginTop: '20px' }}>
    {data.map((zone) => (
      <div key={zone.name} style={{
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: '8px',
        borderBottom: '1px solid #ccc',
        paddingBottom: '4px'
      }}>
        <div><strong>{zone.name}</strong></div>
        <div>{formatTime(zone.seconds)} — {zone.percentage}%</div>
      </div>
    ))}
  </div>
);

export const renderCompactZoneBlock = (data, title, t, zoneTypeKey) => {
  const translateName = (zone) => {
    const label = zone.name.split(' ')[0];
    if (!t || !zoneTypeKey) return zone.name;
    const translated = t(`trainingZones.${zoneTypeKey}.${label.toLowerCase()}`);
    return translated && !translated.includes(zoneTypeKey) ? translated : zone.name;
  };

  const zonesWithTime = data.filter((z) => z.percentage > 0);

  const chartData = {
    labels: zonesWithTime.map((z) => z.name.split(' ')[0]),
    datasets: [
      {
        label: '% time',
        data: zonesWithTime.map((z) => z.percentage),
        backgroundColor: zonesWithTime.map((z) => zoneColors[z.name.split(' ')[0]] || '#7FB3D5'),
        borderRadius: 4,
      },
    ],
  };

  const chartOptions = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: { label: (c) => ` ${c.raw}%` } },
    },
    scales: {
      x: { min: 0, max: 100, ticks: { font: { size: 9 }, callback: (v) => v + '%' } },
      y: { ticks: { font: { size: 9 } } },
    },
  };

  return (
    <div style={{ flex: '1 1 280px', minWidth: 0 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#193255', marginBottom: 5 }}>{title}</div>
      <div style={{ height: 130 }}>
        <Bar data={chartData} options={chartOptions} />
      </div>
      <div style={{ marginTop: 8, fontSize: 11 }}>
        {data.map((zone) => (
          <div key={zone.name} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
            <span style={{ color: '#193255', fontWeight: 600 }}>{translateName(zone)}</span>
            <span style={{ color: '#64748b' }}>{formatTime(zone.seconds)} — {zone.percentage}%</span>
          </div>
        ))}
      </div>
    </div>
  );
};
